import React, { useState } from 'react';
import { Menu, Search, User, Settings, LogOut, Bell } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { Dropdown, DropdownItem, DropdownDivider } from '../design-system';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { LanguageSwitcher } from '../LanguageSwitcher';

export const Header = ({ onMenuToggle, onRightDrawerToggle }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState('');

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // Implement search functionality
    console.log('Search:', searchQuery);
  };

  return (
    <header className="bg-white border-b border-gray-200 sticky top-0 z-50">
      <div className="flex items-center justify-between h-16 px-4 gap-4">
        {/* Left section */}
        <div className="flex items-center gap-4">
          <button
            onClick={onMenuToggle}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle menu"
          >
            <Menu className="h-6 w-6 text-gray-600" />
          </button>
          
          <div className="flex items-center gap-3">
            <div className="h-8 w-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <span className="text-white font-bold text-sm">O</span>
            </div>
            <div className="hidden sm:block">
              <h1 className="text-lg font-semibold text-gray-900">{t('common.appName')}</h1>
              <p className="text-xs text-gray-500">{t('common.appDescription')}</p>
            </div>
          </div>
        </div>

        {/* Center - Search */}
        <form onSubmit={handleSearch} className="hidden md:flex flex-1 max-w-md">
          <div className="relative w-full">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('common.search')}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
            />
          </div>
        </form>

        {/* Right section */}
        <div className="flex items-center gap-2">
          {/* Language Switcher */}
          <LanguageSwitcher />

          {/* Notifications */}
          <button
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors relative"
            aria-label={t('navigation.notifications')}
          >
            <Bell className="h-5 w-5 text-gray-600" />
            <span className="absolute top-1 right-1 h-2 w-2 bg-red-500 rounded-full"></span>
          </button>

          {/* Right drawer toggle (mobile) */}
          <button
            onClick={onRightDrawerToggle}
            className="lg:hidden p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Toggle right menu"
          >
            <Settings className="h-5 w-5 text-gray-600" />
          </button>

          {/* User menu */}
          <Dropdown
            trigger={
              <button className="flex items-center gap-2 p-2 hover:bg-gray-100 rounded-lg transition-colors">
                <div className="h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                  <User className="h-5 w-5 text-primary-600" />
                </div>
                <span className="hidden sm:block text-sm font-medium text-gray-700">
                  {user?.firstName || 'User'}
                </span>
              </button>
            }
            align="right"
          >
            <div className="px-4 py-3 border-b border-gray-200">
              <p className="text-sm font-medium text-gray-900">
                {user?.firstName} {user?.lastName}
              </p>
              <p className="text-xs text-gray-500">{user?.email}</p>
            </div>
            
            <DropdownItem
              icon={<User className="h-4 w-4" />}
              onClick={() => navigate('/profile')}
            >
              {t('navigation.profile')}
            </DropdownItem>
            
            <DropdownItem
              icon={<Settings className="h-4 w-4" />}
              onClick={() => navigate('/settings')}
            >
              {t('navigation.settings')}
            </DropdownItem>
            
            <DropdownDivider />
            
            <DropdownItem
              icon={<LogOut className="h-4 w-4" />}
              onClick={handleLogout}
              danger
            >
              {t('auth.logout')}
            </DropdownItem>
          </Dropdown>
        </div>
      </div>
    </header>
  );
};
